/**
 * The ground a map's layers are painted on. Aerial imagery is a dark, textured
 * ground; every other basemap is light paper. The basemap declares it on the
 * Leaflet container (`data-fm-map-ground`), tokens.css re-resolves the
 * ground-dependent roles there, and a vector layer re-resolves its colours when
 * the ground changes.
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { useMap } from 'react-leaflet';

export type FmMapGround = 'paper' | 'imagery';

const GROUND_ATTRIBUTE = 'data-fm-map-ground';

function readGround(container: HTMLElement): FmMapGround {
  return container.getAttribute(GROUND_ATTRIBUTE) === 'imagery' ? 'imagery' : 'paper';
}

/** Declares the ground of the basemap that is mounted; unmounting clears it. */
export function useDeclareMapGround(ground: FmMapGround): void {
  const map = useMap();
  useLayoutEffect(() => {
    const container = map.getContainer();
    container.setAttribute(GROUND_ATTRIBUTE, ground);
    return () => container.removeAttribute(GROUND_ATTRIBUTE);
  }, [map, ground]);
}

/** The current ground of the enclosing map, updated when the basemap changes. */
export function useMapGround(): { ground: FmMapGround; container: HTMLElement } {
  const map = useMap();
  const container = map.getContainer();
  const [ground, setGround] = useState<FmMapGround>(() => readGround(container));
  useEffect(() => {
    setGround(readGround(container));
    const observer = new MutationObserver(() => setGround(readGround(container)));
    observer.observe(container, { attributes: true, attributeFilter: [GROUND_ATTRIBUTE] });
    return () => observer.disconnect();
  }, [container]);
  return { ground, container };
}
